import { HttpClient } from "@angular/common/http";
import { Component, HostListener, ViewEncapsulation } from "@angular/core";
import { Router } from "@angular/router";
import { environment } from "@environments/environment";
import { AvatarForUserProfile } from "@helpers/avatarHelper";
import { AdminMenu } from "@interfaces/adminMenu.interface";
import { AdminMenuItem } from "@interfaces/adminMenuItem.interface";
import { LoginDetails } from "@interfaces/loginDetails.interface";
import { UserProfile } from "@interfaces/userProfile.interface";
import { AppSettingsService } from "@services/appSettings.service";
import { AuthService } from "@services/auth.service";
import { BannerService } from "@services/banner.service";
import { MenuService } from "@services/menu.service";
import { MenuItemService } from "@services/menuItem.service";
import { PartService } from "@services/part.service";
import { ToastService } from "angular-toastify";

@Component({
	encapsulation: ViewEncapsulation.None,
	moduleId: module.id,
	selector: "adminRoute",
	styleUrls: ["adminRoute.css"],
	templateUrl: "adminRoute.html"
})
export class AdminRouteComponent {
	allGoToEntityTypes = [
		{ apiEndpoint: "WorkOrder", label: "Work Order", menuRoute: "/workOrders", route: "/admin/workOrders", usesLookupId: false },
		{ apiEndpoint: "PurchaseLog", label: "Purchase Log", menuRoute: "/purchaseLogs", route: "/admin/purchaseLogs", usesLookupId: true },
		{ apiEndpoint: "SalesOrder", label: "Sales Order", menuRoute: "/salesOrders", route: "/admin/salesOrders", usesLookupId: true },
		{ apiEndpoint: "Part", label: "Part", menuRoute: "/parts", route: "/admin/parts", usesLookupId: false },
		{ apiEndpoint: "Client", label: "Client", menuRoute: "/clients", route: "/admin/clients", usesLookupId: false },
		{ apiEndpoint: "UserProfile", label: "User", menuRoute: "/users", route: "/admin/users", usesLookupId: false },
		{ apiEndpoint: "Supplier", label: "Supplier", menuRoute: "/suppliers", route: "/admin/suppliers", usesLookupId: false },
		{ apiEndpoint: "Invoice", label: "Invoice", menuRoute: "/invoices", route: "/admin/invoices", usesLookupId: true },
		{ apiEndpoint: "DeliveryNote", label: "Delivery Note", menuRoute: "/deliveryNotes", route: "/admin/deliveryNotes", usesLookupId: true },
		{ apiEndpoint: "Machine", label: "Machine", menuRoute: "/machines", route: "/admin/machines", usesLookupId: false },
		{ apiEndpoint: "NonConformanceReport", label: "NCR", menuRoute: "/nonConformanceReports", route: "/admin/nonConformanceReports", usesLookupId: true }
	];
	avatarUrl = "";
	bannerText = "";
	companyName = "";
	copyright = "";
	disableMobileMenu = false;
	displayBanner = false;
	goToEntityType = "";
	goToId = "";
	isDesktop = window.innerWidth >= 1024;
	isDrawerOpen = false;
	loggedIn = false;
	loginButtonOptions = { onClick: () => this.login(), text: "Login", type: "success", useSubmitBehavior: true };
	loginDetails: LoginDetails = new LoginDetails();
	menuItems: AdminMenuItem[] = [];
	mobileMenuOpen = false;
	partSearchResults: any[] = [];
	partSearchTerm = "";
	parts: any[] = [];
	permittedRoutes: string[] = [];
	showGoToDialog = false;
	showLoginForm = false;
	showPartSearch = false;
	showSidebar = true;
	staging = false;
	subMenus: AdminMenu[] = [];
	systemName = "BPL Flow";
	userMenuItems: AdminMenuItem[] = [
		{ category: "", icon: "fal fa-user-cog", route: "user-settings", sortOrder: 998, text: "Settings" },
		{ category: "", icon: "fal fa-sign-out", route: "logout", sortOrder: 999, text: "Logout" }
	];
	userProfile: UserProfile = new UserProfile();
	validationErrors = { login: "", password: "" };

	constructor(private http: HttpClient, private router: Router, private toastService: ToastService, private appSettingsService: AppSettingsService, private authService: AuthService, private bannerService: BannerService, private menuService: MenuService, private menuItemService: MenuItemService, private partService: PartService) {
		this.companyName = this.getEnvironmentProperty("companyLongName");
		this.copyright = "© Copyright BPL Engineering Group " + new Date().getFullYear() + " | v" + environment.build.version;
		if (environment.build.production === false) {
			this.staging = true;
		}
		if (router.url.includes("complete-account") || router.url.includes("webapps") || router.url.includes("forgotPassword") || router.url.includes("woview") || router.url.includes("clocking") || router.url.includes("machineOverview") || router.url.includes("toolroomOverview") || router.url.includes("andon") || router.url.includes("woscan") || router.url.includes("operator") || router.url.includes("supervisor") || router.url.includes("resetPassword")) {
			this.loggedIn = false;
			this.showLoginForm = false;
			this.showSidebar = false;
			this.disableMobileMenu = true;
		} else {
			this.authService.authCheck.subscribe((value) => {
				if (value === true) {
					this.loggedIn = true;
					this.getMenuItems();
					this.getBannerText();
					this.getParts();
					this.getUserProfile();
				} else {
					this.loggedIn = false;
					this.showLoginForm = true;
					this.userProfile = new UserProfile();
					this.toastService.error("You have been logged out.");
				}
			});
		}
		if (this.windowWidthCheck()) {
			this.isDrawerOpen = false;
		}
		// Print routes render just the printable sheet — hide the admin sidebar/chrome.
		if (router.url.endsWith("/print")) {
			this.showSidebar = false;
		}
	}

	closeGoToDialog() {
		this.showGoToDialog = false;
		this.goToId = "";
	}

	closePartSearch() {
		this.showPartSearch = false;
		this.partSearchTerm = "";
		this.partSearchResults = [];
	}

	// eslint-disable-next-line @typescript-eslint/no-empty-function
	forgottenPassword() { }

	getBannerText() {
		this.bannerService.getBanner()
			.subscribe(
				(res: any) => {
					const bannerDetails = res.response;
					this.displayBanner = bannerDetails.display;
					this.bannerText = bannerDetails.text;
				},
				(err) => console.log(err)
			);
	}

	get goToEntityTypes() {
		if (this.permittedRoutes.length === 0) return this.allGoToEntityTypes;
		return this.allGoToEntityTypes.filter((et) => this.permittedRoutes.includes(et.menuRoute));
	}

	getEnvironmentProperty(property: string) {
		// @ts-ignore // Required to get around having to iterate through the object
		return environment["build"][property];
	}

	getMenuItems() {
		this.menuItemService.getUserMenuItems("main")
			.subscribe(
				(res: any) => {
					this.menuItems = res.response;
					this.getSubMenus();
				},
				(err) => console.log(err)
			);
	}

	getParts() {
		this.partService.getAll()
			.subscribe(
				(res: any) => {
					this.parts = res.response?.items || res.response || [];
					this.partService.allParts.next(this.parts);
				},
				(err) => console.log(err)
			);
	}

	getSubMenus() {
		this.menuService.getUserMainMenu()
			.subscribe(
				(res: any) => {
					this.subMenus = res.response;
					this.subMenus.forEach((subMenu) => {
						this.menuItems.push({ category: "SubMenu", icon: subMenu.icon, route: `/menu/${subMenu.slug}`, sortOrder: 999, text: subMenu.name });
					});
				},
				(err) => console.log(err)
			);
		this.menuItemService.getUserMenuItems("administration")
			.subscribe(
				(res: any) => {
					this.permittedRoutes = (res.response || []).map((item: any) => item.route);
				}
			);
	}


	getUserProfile() {
		this.authService.getCurrentUserProfile()
			.subscribe(
				(res: any) => {
					this.userProfile = res.response;
					this.avatarUrl = AvatarForUserProfile(this.userProfile);
				},
				(err) => console.log(err)
			);
	}

	goToEntity() {
		if (!this.goToId || !this.goToEntityType) return;
		const entity = this.allGoToEntityTypes.find((et) => et.menuRoute === this.goToEntityType);
		if (!entity) return;

		if (entity.usesLookupId) {
			this.http.get(`${environment.server.apiURL}/${entity.apiEndpoint}/${this.goToId}`)
				.subscribe(
					(res: any) => {
						if (res.response && res.response.lookupId) {
							this.router.navigate([`${entity.route}/${res.response.lookupId}`]);
						} else {
							this.toastService.error("Record not found");
						}
						this.closeGoToDialog();
					},
					() => {
						this.toastService.error("Record not found");
						this.closeGoToDialog();
					}
				);
		} else {
			this.router.navigate([`${entity.route}/${this.goToId}`]);
			this.closeGoToDialog();
		}
	}

	goToPart(part: any) {
		this.router.navigate([`/admin/parts/${part.id}`]);
		this.closePartSearch();
	}

	isActiveRoute(route: string) {
		return location.href.endsWith(`${environment.build.adminRoute}${route}`);
	}

	login() {
		this.validationErrors = { login: "", password: "" };

		this.authService.loginAdmin(this.loginDetails)
			.subscribe(
				() => {
					this.getMenuItems();
					this.loggedIn = true;
					this.showLoginForm = false;
					this.toastService.success("Welcome Back!");
					this.getBannerText();
					this.getParts();
					this.getUserProfile();
				},
				(err) => {
					this.validationErrors.login = err;
					this.toastService.error(err);
				}
			);
		return true;
	}

	// eslint-disable-next-line @typescript-eslint/no-empty-function
	logout() {
		this.loggedIn = false;
		this.showLoginForm = true;
		this.loginDetails = new LoginDetails();
		this.appSettingsService.set("accessToken", null);
		this.appSettingsService.set("accessTokenExpiryTimeMilliseconds", 0);
		this.appSettingsService.set("groups", null);
		this.router.navigate(["admin"]);
	}

	navigate(menuItem: AdminMenuItem) {
		if (menuItem.route !== "logout") this.router.navigate([`admin/${menuItem.route}`]);
		else this.logout();
	}

	navigateUrl(url: string) {
		this.router.navigate([url]);
	}

	ngOnDestroy() {
		window.removeEventListener("resize", this.updateViewport);
	}

	ngOnInit() {
		window.addEventListener("resize", this.updateViewport);
	}

	@HostListener("window:keydown", ["$event"])
	onKeydown(event: KeyboardEvent) {
		if (event.ctrlKey && event.key === "g") {
			event.preventDefault();
			if (this.loggedIn) this.openGoToDialog();
		}
		if (event.ctrlKey && event.key === "k") {
			event.preventDefault();
			if (this.loggedIn && this.permittedRoutes.includes("/parts")) this.openPartSearch();
		}
		if (event.key === "Escape") {
			if (this.showGoToDialog) this.closeGoToDialog();
			if (this.showPartSearch) this.closePartSearch();
		}
	}

	onPartSearchInput() {
		if (!this.partSearchTerm || this.partSearchTerm.trim().length < 2) {
			this.partSearchResults = [];
			return;
		}
		const term = this.partSearchTerm.trim().toLowerCase();
		this.partSearchResults = this.parts.filter((p: any) => p.partNumber && p.partNumber.toLowerCase().includes(term)).slice(0, 15);
	}

	openGoToDialog() {
		this.showGoToDialog = true;
		this.goToId = "";
		const available = this.goToEntityTypes;
		this.goToEntityType = available.length > 0 ? available[0].menuRoute : "";
		setTimeout(() => {
			const input = document.querySelector(".goto-id-input") as HTMLInputElement;
			if (input) input.focus();
		});
	}

	openPartSearch() {
		this.showPartSearch = true;
		this.partSearchTerm = "";
		this.partSearchResults = [];
		setTimeout(() => {
			const input = document.querySelector(".part-search-input") as HTMLInputElement;
			if (input) input.focus();
		});
	}

	toggleMobileMenu() {
		this.mobileMenuOpen = !this.mobileMenuOpen;
	}

	toggleSideDrawer() {
		this.isDrawerOpen = !this.isDrawerOpen;
	}

	updateViewport = () => {
		this.isDesktop = window.innerWidth >= 1024;
	};

	windowWidthCheck() {
		return window.innerWidth <= 1366;
	}
}